import type { SubmissionFile } from './types.ts';

export type ExpiryState = 'none' | 'ok' | 'expiring' | 'expired';

// Files within this many days of `expires_at` are flagged as expiring.
export const EXPIRY_WARNING_DAYS = 14;

export function expiryState(
  file: Pick<SubmissionFile, 'expires_at'>,
  now: Date = new Date(),
): ExpiryState {
  if (!file.expires_at) return 'none';
  const when = new Date(file.expires_at).getTime();
  if (Number.isNaN(when)) return 'none';
  const nowMs = now.getTime();
  if (when <= nowMs) return 'expired';
  const days = (when - nowMs) / (24 * 60 * 60 * 1000);
  if (days <= EXPIRY_WARNING_DAYS) return 'expiring';
  return 'ok';
}

// Convert an ISO timestamp string from the API to the `YYYY-MM-DD` form a
// `<input type="date">` accepts, or '' if absent.
export function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Convert a `YYYY-MM-DD` value back to an ISO timestamp at end-of-day UTC
// (so the file is considered valid through the whole calendar day).
export function fromDateInput(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(`${value}T23:59:59.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

// Format a date-only value for display. `expires_at` is a *calendar date* the
// vendor picked, stored as an end-of-day-UTC timestamp (see fromDateInput). It
// must therefore be rendered in UTC too, so the day shown equals the day picked
// in every timezone — a plain toLocaleDateString() reads local time and, in
// UTC+ zones (e.g. Australia), rolls 23:59:59Z into the next calendar day.
// `timeZone: 'UTC'` is forced last so a caller can't accidentally override it.
// NOTE: only for date-only values; real instants (signed_at, created_at, …)
// belong in local time and must NOT use this.
export function formatDateOnly(
  iso: string | null,
  opts: Intl.DateTimeFormatOptions = {},
  locale?: string,
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { ...opts, timeZone: 'UTC' });
}
